"use client";

import { Link } from "@/i18n/routing";
import { Search, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useTranslations } from "next-intl";
import {
  useProductFilters,
  ActiveFilterPills,
} from "@/components/product/ProductFilters";

export interface NoResultsStateProps {
  /** Link to the "browse all" page for this category, e.g. "/wigs" */
  browseAllHref: string;
  /** Label for the "browse all" link, e.g. "wigs" */
  browseAllLabel: string;
}

export function NoResultsState({
  browseAllHref,
  browseAllLabel,
}: NoResultsStateProps) {
  const t = useTranslations("product");
  const {
    activeFilters,
    handleRemoveFilter,
    handleClearAll,
    hasActiveFilters,
  } = useProductFilters();

  return (
    <div className="flex flex-col items-center py-20 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-cream">
        <Search className="h-7 w-7 text-brand-muted" />
      </div>

      <h3 className="mt-6 font-display text-lg text-brand-dark">
        {hasActiveFilters
          ? t("noMatchFilters")
          : t("noProducts")}
      </h3>

      {hasActiveFilters && (
        <p className="mt-2 max-w-md text-sm text-brand-muted">
          {t("tryAdjustFilters")}
        </p>
      )}

      {/* Active filter pills with remove option */}
      {hasActiveFilters && (
        <div className="mt-5 max-w-lg">
          <ActiveFilterPills
            filters={activeFilters}
            onRemove={handleRemoveFilter}
            onClearAll={handleClearAll}
            clearAllLabel={t("clearAll")}
          />
        </div>
      )}

      {/* Actions */}
      <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
        {hasActiveFilters && (
          <Button variant="primary" size="sm" onClick={handleClearAll}>
            <X className="h-3.5 w-3.5" />
            {t("clearAllFilters")}
          </Button>
        )}
        <Button variant="secondary" size="sm" asChild>
          <Link href={browseAllHref}>
            {t("browseAll", { category: browseAllLabel })}
          </Link>
        </Button>
      </div>
    </div>
  );
}
